import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  Box,
  Button,
  ButtonVariants,
  FormControlSizes,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useCallbackRef,
  useMixpanel,
} from '@talos/kyoko';
import type { MultilegTabData } from '../../providers/MarketTabs.types';
import {
  clear,
  selectIsEditing,
  selectIsLoading,
  selectIsValid,
  selectOriginalResolvedSymbolOnEdit,
  selectResolvedSymbol,
  setIsEditing,
  setIsLoading,
} from './MultilegComboSlice';
import { MultilegFlipLegsButton } from './MultilegFlipLegsButton';
import { useMultilegComboService } from './hooks';

export function MultilegComboActions({
  panelID,
  updateMultilegTabData,
}: {
  panelID: string;
  updateMultilegTabData: (tabData: MultilegTabData, label: string) => void;
}) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();

  const service = useMultilegComboService(panelID, updateMultilegTabData);

  const isValid = useAppStateSelector(state => selectIsValid(state, panelID));
  const isLoading = useAppStateSelector(state => selectIsLoading(state, panelID));
  const isEditing = useAppStateSelector(state => selectIsEditing(state, panelID));
  const resolvedSymbol = useAppStateSelector(state => selectResolvedSymbol(state, panelID));
  const originalResolvedSymbolOnEdit = useAppStateSelector(state => selectOriginalResolvedSymbolOnEdit(state, panelID));

  const handleReadyToTradeClick = useCallbackRef(async () => {
    dispatch(setIsLoading({ isLoading: true, panelID }));
    await service?.upsertMultilegSecurityAndPrimeOMS(true);
  });

  const handleCancel = useCallbackRef(() => {
    dispatch(setIsEditing({ isEditing: false, panelID }));
  });

  const handleEditOrCancel = useCallbackRef(async () => {
    if (isEditing) {
      dispatch(setIsEditing({ isEditing: false, panelID }));
    } else {
      mixpanel.track(MixpanelEvent.EditMultileg, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegComboScreen,
      });
      dispatch(setIsEditing({ isEditing: true, panelID }));
    }
  });

  return (
    <HStack gap="spacing12" p="spacing8" justifyContent="flex-end" background="colors.gray.020" my={1}>
      <Box flex={1}>
        <MultilegFlipLegsButton panelID={panelID} />
      </Box>
      {resolvedSymbol && !isEditing ? (
        <Button
          onClick={handleEditOrCancel}
          disabled={isLoading}
          variant={ButtonVariants.Default}
          size={FormControlSizes.Small}
          id="multileg-deepdive-edit"
          data-testid="multileg-edit-symbol-button"
        >
          Edit Symbol
        </Button>
      ) : null}
      {originalResolvedSymbolOnEdit ? (
        <Button
          onClick={handleCancel}
          disabled={isLoading}
          variant={ButtonVariants.Default}
          size={FormControlSizes.Small}
          data-testid="multileg-cancel-button"
        >
          Cancel
        </Button>
      ) : null}
      {!resolvedSymbol ? (
        <Button
          onClick={() => dispatch(clear(panelID))}
          disabled={isLoading}
          variant={ButtonVariants.Default}
          size={FormControlSizes.Small}
        >
          Clear
        </Button>
      ) : null}
      <Button
        onClick={handleReadyToTradeClick}
        disabled={!isValid || isLoading}
        loading={isLoading}
        variant={resolvedSymbol ? ButtonVariants.Primary : ButtonVariants.Positive}
        size={FormControlSizes.Small}
        data-testid={
          isEditing
            ? 'multileg-update-symbol-button'
            : resolvedSymbol
              ? 'multileg-prime-order-button'
              : 'multileg-create-symbol-button'
        }
      >
        {isEditing ? 'Update Symbol' : resolvedSymbol ? 'Prime Order' : 'Create Symbol'}
      </Button>
    </HStack>
  );
}
